import {
  IconBook2,
  IconBulb,
  IconCalendar,
  IconChartBar,
  IconClipboardCheck,
  IconPencil,
  IconPresentation,
  IconPuzzle,
  IconReportAnalytics,
  IconSchool,
  IconTarget,
  IconUsers,
} from '@tabler/icons-react'

// The hub's `icon`/`color` fields in Sanity drive the Resource Center's visual
// identity; the slug map below keeps working as a fallback for hubs that
// predate those fields. Unknown slugs fall back to a neutral book icon so
// newly-created hubs still render sensibly.
export type HubStyle = {
  icon: React.ComponentType<{ size?: number }>
  color: string
}

const PLANNING: HubStyle = { icon: IconBulb, color: 'turquoise' }
const ASSESSMENT: HubStyle = { icon: IconClipboardCheck, color: 'blue' }
const REPORTING: HubStyle = { icon: IconReportAnalytics, color: 'violet' }
const DATA_ANALYTICS: HubStyle = { icon: IconChartBar, color: 'orange' }

// Keyed by hub slug. Slugs differ per language (each translation is its own
// document), so both the English and Portuguese slugs map to the same style.
const HUB_STYLES: Record<string, HubStyle> = {
  planning: PLANNING,
  planejamento: PLANNING,
  assessment: ASSESSMENT,
  avaliacao: ASSESSMENT,
  reporting: REPORTING,
  relatorios: REPORTING,
  'data-analytics': DATA_ANALYTICS,
  'analise-de-dados': DATA_ANALYTICS,
}

const FALLBACK_STYLE: HubStyle = { icon: IconBook2, color: 'turquoise' }

// Palettes available in the app's Mantine theme (custom turquoise/purple +
// defaults). Guards against typos in the Studio `color` field, which would
// otherwise break theme color lookups. The Studio's `primary` color maps to
// `turquoise`, this app's equivalent of the prototype's primary palette.
const KNOWN_COLORS = new Set([
  'turquoise',
  'purple',
  'blue',
  'violet',
  'orange',
  'teal',
  'cyan',
  'indigo',
  'grape',
  'pink',
  'red',
  'green',
  'lime',
  'yellow',
  'gray',
])

// Tabler icon names (tabler.io/icons, kebab-case) offered by the Studio's
// closed `icon` list, mapped to their components. Both lists must stay in
// sync: a name missing here falls back to the slug map / book icon.
const KNOWN_ICONS: Record<string, HubStyle['icon']> = {
  bulb: IconBulb,
  'clipboard-check': IconClipboardCheck,
  'report-analytics': IconReportAnalytics,
  'chart-bar': IconChartBar,
  'book-2': IconBook2,
  school: IconSchool,
  users: IconUsers,
  calendar: IconCalendar,
  target: IconTarget,
  puzzle: IconPuzzle,
  pencil: IconPencil,
  presentation: IconPresentation,
}

// The hub's `color`/`icon` fields in Sanity win when they hold known values;
// the slug map keeps working as a fallback for hubs without them.
export const getHubStyle = (slug: string, color?: string | null, icon?: string | null): HubStyle => {
  const base = HUB_STYLES[slug] ?? FALLBACK_STYLE
  const themeColor = color === 'primary' ? 'turquoise' : color

  return {
    icon: (icon && KNOWN_ICONS[icon]) || base.icon,
    color: themeColor && KNOWN_COLORS.has(themeColor) ? themeColor : base.color,
  }
}
